import React, { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";

import {
    EventsPage,
    HomePage,
    NotAccess,
    NotFound404,
    ProfileAddPage,
    ProfileInterestsPage,
    ProfileLifestylesPage,
    ProfilePhotosPage,
    ProfilesPage,
    MyPage,
    FaqPage,
    ContactPage
} from "../../pages";
import { HelpPage } from "../../pages/help/help";

import Layout from "../layout/layout";
import Loader from "../loader/loader";
import Modal from "../modal/modal";
import ProfileSuccess from "../profile-success/profile-success";
import EventPage from "../../pages/event/event";
import ProfilePage from "../../pages/profile/profile";
import ProfileInfoPage from "../../pages/profile-info-page/profile-info-page";
import { ProtectedRouteElement } from "../protected-route-element/protected-route-element";
import ProfileModeration from "../profile-moderation/profile-moderation";

import { generateTestInitData } from "../../utils/testTelegramData";
import { useGetDictQuery } from "../../services/store/api/dictApi";
import { useGetEventsQuery } from "../../services/store/api/eventsApi";
import { useGetProfileQuery, useLoginMutation } from "../../services/store/api/userApi";
import { parseScoutCode, parseStartApp } from "../../utils/parseStartApp";
import { OptionsPage } from '../../pages/options/options';
import { NotificationSettingsPage } from '../../pages/notification-settings/notification-settings';
import type { RootState } from "../../services/store";

const protectedRoute = (element: React.ReactNode, requireProfile = false, requireActiveProfile = false) => (
    <ProtectedRouteElement requireProfile={requireProfile} requireActiveProfile={requireActiveProfile}>
        {element}
    </ProtectedRouteElement>
);

function App() {


    const tg = window.Telegram?.WebApp;

    const initData = useMemo(() => {
        return process.env.NODE_ENV === "development"
            ? generateTestInitData()
            : tg?.initData;
    }, [tg]);

    const location = useLocation();
    const navigate = useNavigate();
    const backgroundLocation = location.state?.backgroundLocation;

    const [login, { isLoading: loginLoading }] = useLoginMutation();

    const { isAuthChecked, userTelegram } = useSelector((state: RootState) => state.user);
    const lifestyleCategories = useSelector((state: RootState) => state.dict.lifestyleCategories);
    const shouldLoadProfile = isAuthChecked && Boolean(userTelegram) && !loginLoading;

    const {
        data: profileData,
        isLoading: profileLoading
    } = useGetProfileQuery(undefined, {
        skip: !shouldLoadProfile
    });

    const profile = profileData?.profile ?? null;

    const { isLoading: dictLoading } = useGetDictQuery();
    const { isLoading: eventsLoading } = useGetEventsQuery();

    const isLoggedInRef = useRef(false);

    useEffect(() => {
        if (!initData || isLoggedInRef.current) return;

        isLoggedInRef.current = true;
        login({ initData });

        if (!tg) return;

        tg.ready();
        tg.expand();
        tg.MainButton.hide();

    }, [login, initData, tg])

    useEffect(() => {
        if (!tg) return;

        // Show Back button outside root pages
        // Allows users to return from nested screens
        const shouldShowBackButton = location.pathname !== '/' && location.pathname !== '/home' && location.pathname !== '/events';

        if (shouldShowBackButton) {
            tg.BackButton.show();
        } else {
            tg.BackButton.hide();
        }

        // Back button handler
        const onBackClick = () => {
            navigate(-1); // Browser back
        };

        tg.BackButton.onClick(onBackClick);

        // Cleanup listener on unmount or dependency changes
        return () => {
            tg.BackButton.offClick(onBackClick);
        };
    }, [location, navigate, tg]);

    const startAppParam = useMemo(() => {
        return tg?.initDataUnsafe?.start_param;
    }, [tg]);

    useEffect(() => {
        if (!startAppParam) return;

        localStorage.setItem("startapp_param", startAppParam);

        const scoutCode = parseScoutCode(startAppParam);
        if (scoutCode) {
            localStorage.setItem("scout_code", scoutCode);
        }
    }, [startAppParam]);

    const startAppHandledRef = useRef(false);

    useEffect(() => {
        if (startAppHandledRef.current) return;
        if (!profile && profileLoading) return;

        const target = parseStartApp(startAppParam);

        if (target) {
            startAppHandledRef.current = true;
            navigate(target, { replace: true });
        }
    }, [
        startAppParam,
        profileLoading,
        profile,
        navigate
    ]);
    useEffect(() => {
        if (location.pathname !== "/") return;
        if (loginLoading || profileLoading) return;

        // If startapp exists, skip default redirect
        if (startAppParam) return;

        let target: string | null = null;

        if (!profile) {
            target = "/home";
        } else if (!profile.interests?.length) {
            target = "/profile-interests";
        } else if (lifestyleCategories.length && !profile.lifestyles?.length) {
            target = "/profile-lifestyle";
        } else if (!profile.photos?.length) {
            target = "/profile-photos";
        } else {
            target = "/events";
        }

        if (target) {
            navigate(target, { replace: true });
        }
    }, [
        loginLoading,
        profileLoading,
        profile,
        lifestyleCategories.length,
        location.pathname,
        navigate,
        startAppParam
    ]);


    if (
        dictLoading ||
        loginLoading ||
        profileLoading ||
        eventsLoading
    ) {
        return <Loader />;
    }

    return (
        <>

            <Routes location={backgroundLocation || location}>
                <Route path="/" element={<Layout />}>
                    <Route index element={<Loader />} />

                    <Route path="/home" element={protectedRoute(<HomePage />)} />

                    <Route path="/options" element={protectedRoute(<OptionsPage />, true)} />
                    <Route path="/options/notifications" element={protectedRoute(<NotificationSettingsPage />, true)} />
                    <Route path="/faq" element={protectedRoute(<FaqPage />, true)} />
                    <Route path="/help" element={protectedRoute(<HelpPage />, true)} />
                    <Route path="/contact" element={protectedRoute(<ContactPage />)} />
                    <Route path="/profile" element={protectedRoute(<ProfilePage />, true)} />
                    <Route path="/profile/info" element={protectedRoute(<ProfileInfoPage />, true)} />
                    <Route path="/profile-moderation" element={protectedRoute(<ProfileModeration />, true)} />
                    <Route path="/my" element={protectedRoute(<MyPage />, true, true)} />

                    <Route path="/profile/interests" element={protectedRoute(<ProfileInterestsPage withProfileMenu />)} />
                    <Route path="/profile/lifestyle" element={protectedRoute(<ProfileLifestylesPage withProfileMenu />)} />
                    <Route path="/profile/photos" element={protectedRoute(<ProfilePhotosPage withProfileMenu />)} />

                    <Route path="/profile-add" element={protectedRoute(<ProfileAddPage />)} />
                    <Route path="/profile-interests" element={protectedRoute(<ProfileInterestsPage />)} />
                    <Route path="/profile-lifestyle" element={protectedRoute(<ProfileLifestylesPage />)} />

                    <Route path="/profile-photos" element={protectedRoute(<ProfilePhotosPage />)} />


                    <Route path="/events" element={protectedRoute(<EventsPage />)} />
                    <Route path="/events/:id" element={protectedRoute(<EventPage />)} />
                    <Route path="/profiles" element={protectedRoute(<ProfilesPage />, true, true)} />
                    <Route path="/profiles/:id" element={protectedRoute(<ProfilesPage />, true, true)} />

                    <Route path="/not-access" element={<NotAccess />} />
                    <Route path="*" element={<NotFound404 />} />
                </Route>
            </Routes>

            {backgroundLocation && (
                <Routes>
                    <Route
                        path="/profile-success"
                        element={
                            <Modal header="Профиль создан" onClose={() => navigate("/events")}> 
                                <ProfileSuccess />
                            </Modal>
                        }
                    />
                </Routes>
            )}
        </>
    );
}

export default App;
